import { FAQ_ITEMS } from '@/lib/seo-content'

/**
 * Long-form content rendered below the calculator. Closes the three content
 * gaps that a pure calculator UI leaves open:
 *
 *   - SEO: substantive crawlable prose + a clean heading hierarchy
 *     (page <h1> → these <h2>s → FAQ <h3>s).
 *   - AEO: question-style headings with concise, extractable answers — the
 *     shape answer engines (ChatGPT, Perplexity, Google AI) lift verbatim.
 *   - Compliance: the FAQ is now VISIBLE, rendered from the same FAQ_ITEMS
 *     that feed the FAQPage JSON-LD, so structured data matches on-page copy.
 */
export function AboutSection() {
	return (
		<section aria-label="About the Fuel Estimator" className="border-t border-border bg-surface/30">
			<div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
				<h2 className="font-heading text-xl text-text-primary sm:text-2xl">
					How does the Fuel Estimator calculate fuel cost?
				</h2>
				<div className="mt-4 space-y-4 font-prose text-base leading-relaxed text-text-secondary">
					<p>
						Most fuel calculators multiply distance by a single flat MPG. This one samples the
						elevation profile along your actual route and adjusts fuel burn segment by segment — a
						loaded Prevost tour bus climbing a mountain grade burns far more than the same bus
						cruising flat highway, and a national average hides that. Grade penalties scale by
						vehicle weight class, so a semi is affected more than a Sprinter van.
					</p>
					<p>
						Diesel and gas prices vary widely from state to state. Instead of one national number,
						it prices every state your route passes through using live data from the U.S. Energy
						Information Administration (EIA) — state-level where the EIA publishes it, and the
						regional PADD average otherwise. The result is a per-state breakdown that reflects what
						you&apos;ll actually pay along the way.
					</p>
					<p>
						Pick from six touring presets — sedan/SUV, 15-passenger van, Sprinter van, 26-foot box
						truck, Prevost tour bus, or full semi — or override the MPG with your own measured
						figure, which persists across sessions. Which state each mile falls in is computed
						offline against bundled U.S. boundary polygons, so the estimate keeps working even when
						upstream map services are slow.
					</p>
				</div>

				<h2 className="mt-10 font-heading text-xl text-text-primary sm:text-2xl">
					Frequently asked questions
				</h2>
				<div className="mt-4 space-y-6">
					{FAQ_ITEMS.map((item) => (
						<div key={item.question}>
							<h3 className="font-heading text-base text-text-primary">{item.question}</h3>
							<p className="mt-1.5 font-prose text-base leading-relaxed text-text-secondary">
								{item.answer}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}
